<template>
  <component
    :is="to ? 'router-link' : 'button'"
    :to="to"
    :type="to ? undefined : type"
    class="ds-btn"
    :class="[`ds-btn--${variant}`, { 'ds-btn--block': block, 'ds-btn--loading': loading }]"
    :disabled="to ? undefined : disabled || loading"
    :aria-busy="loading || undefined"
  >
    <span v-if="loading" class="ds-btn__spinner" aria-hidden="true" />
    <slot name="icon" />
    <span class="ds-btn__label"><slot /></span>
    <span v-if="variant === 'link'" class="ds-btn__arrow" aria-hidden="true">→</span>
  </component>
</template>

<script setup lang="ts">
/**
 * Cabinet button (03_ДИЗАЙН_СИСТЕМА.md §6). primary — bronze fill (AA: #6E5A42 behind cream text); secondary — surface
 * with a light bronze outline; onyx — the dark button of the concierge cards; on-dark / on-dark-outline — the two hero
 * buttons on the dark home header; danger / danger-solid — blocking; link — bronze text with an arrow.
 */
withDefaults(defineProps<{
  variant?: 'primary' | 'secondary' | 'onyx' | 'on-dark' | 'on-dark-outline' | 'danger' | 'danger-solid' | 'link';
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  to?: string | Record<string, unknown>;
}>(), { variant: 'primary', type: 'button' });
</script>

<style scoped lang="scss">
.ds-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 46px;
  padding: 13px 19px;
  border-radius: 14px;
  border: 1px solid transparent;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out), transform var(--dur-fast);
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: .5; cursor: default; }
  :deep(svg) { flex: 0 0 auto; }
}
.ds-btn--block { display: flex; width: 100%; }

.ds-btn--primary { background: var(--bronze-strong); color: var(--cream); box-shadow: var(--shadow-primary); }
.ds-btn--secondary { background: var(--ds-surface); color: var(--text); border-color: #8b735526; }
.ds-btn--onyx { background: var(--onyx); color: var(--cream); }
.ds-btn--on-dark { background: var(--cream); color: var(--onyx); }
.ds-btn--on-dark-outline { background: transparent; color: var(--cream); border-color: #f5f1e866; }
.ds-btn--danger { background: var(--ds-surface); color: var(--red); border-color: #dfc7c0; border-radius: 11px; }
.ds-btn--danger-solid { background: var(--red); color: #fff; border-radius: 11px; }
.ds-btn--link {
  min-height: 0;
  padding: 4px 0;
  background: none;
  color: var(--bronze-text);
  font-size: 13px;
  gap: 6px;
}

.ds-btn__spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: ds-spin .8s linear infinite;
}
@keyframes ds-spin { to { transform: rotate(360deg); } }
</style>
